import type { ComponentPropsWithoutRef } from "react";
import { cn } from "@/lib/utils";

/**
 * Page-level width convention (redesign §3.7 / D10). The AppShell `<main>`
 * already provides the page padding (`p-6`) — this container only centers and
 * caps:
 *
 * - `wide` — data/table/dashboard pages: use the full available width with a
 *   hard ceiling so ultra-wide monitors don't produce absurd line lengths.
 * - `form` — settings pages: a form-friendly ceiling (1536px), wider than the
 *   old `max-w-5xl` but still comfortable for labeled inputs.
 *
 * Vertical rhythm defaults to `space-y-6`; pages with a tighter rhythm pass
 * e.g. `className="space-y-4"` (tailwind-merge resolves the conflict).
 */
export const pageContainerVariants = {
  wide: "max-w-[1800px] 2xl:max-w-[2000px]",
  form: "max-w-[1536px]",
} as const;

export type PageContainerVariant = keyof typeof pageContainerVariants;

interface PageContainerProps extends ComponentPropsWithoutRef<"div"> {
  variant?: PageContainerVariant;
  /**
   * Workbench pages that fill the viewport instead of scrolling with it
   * (catalogue, requirement detail, config console).
   *
   * This is the replacement for the `h-[calc(100vh-9.5rem)]` magic heights
   * (R8): `<main>` is a `flex flex-col min-h-0` scroll container of definite
   * height, so `h-full` here resolves against the real content box whatever the
   * header currently measures. `min-h-0` lets the inner panes shrink and scroll
   * themselves; the vertical rhythm is dropped because a full-height page
   * composes with `gap-*` on its own flex column, not with sibling margins.
   *
   * ⚠ `cn()` is `tailwind-merge`: a caller-supplied `min-h-*` **replaces**
   * `min-h-0`, it does not add to it. A full-height page that wants a floor
   * must therefore clamp it against the space that exists —
   * `min-h-[min(480px,100%)]`, never a bare `min-h-[480px]`. `100%` resolves
   * against `<main>`'s content box, which is definite (the shell is `h-dvh`,
   * `<main>` is `flex-1 min-h-0`), so above the floor the page is roomy and
   * below it the panes shrink and scroll themselves instead of pushing a page
   * scrollbar next to the ones they already have.
   */
  fullHeight?: boolean;
}

export function PageContainer({
  variant = "wide",
  fullHeight = false,
  className,
  ...props
}: PageContainerProps) {
  return (
    <div
      data-full-height={fullHeight ? "true" : undefined}
      className={cn(
        "mx-auto w-full",
        fullHeight ? "flex h-full min-h-0 flex-col" : "space-y-6",
        pageContainerVariants[variant],
        className,
      )}
      {...props}
    />
  );
}
